import type * as React from "react";
import { GraduationCap } from "lucide-react";
import { NavLink } from "react-router";
import { cn } from "../../lib/cn.js";
import type { NavGroup } from "../navigation/nav-types.js";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet.js";

export interface MobileNavProps {
  groups: NavGroup[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** School/campus `ScopeSelector`s — the header hides them below `sm`, so the drawer carries them there. */
  scopeSelectors?: React.ReactNode;
}

export function MobileNav({ groups, open, onOpenChange, scopeSelectors }: MobileNavProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="left" className="w-[280px] p-0">
        <SheetHeader className="flex h-16 flex-row items-center gap-2 border-b border-border px-4">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-[var(--radius-control)] bg-primary text-primary-foreground">
            <GraduationCap className="size-[18px]" />
          </span>
          <SheetTitle>Academia HUB</SheetTitle>
        </SheetHeader>
        {scopeSelectors && (
          <div className="flex flex-col items-start gap-1.5 border-b border-border px-3 py-3 sm:hidden" role="group" aria-label="School and campus">
            {scopeSelectors}
          </div>
        )}
        <nav className="flex-1 space-y-4 overflow-y-auto px-3 py-4" aria-label="Primary">
          {groups.map((group) => (
            <div key={group.label} className="space-y-1">
              <p className="px-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{group.label}</p>
              <ul className="space-y-0.5">
                {group.items.map((item) => (
                  <li key={item.href}>
                    <NavLink
                      to={item.href}
                      onClick={() => onOpenChange(false)}
                      className={({ isActive }) =>
                        cn(
                          "flex items-center gap-3 rounded-[var(--radius-control)] px-2.5 py-2 text-sm font-medium transition-colors hover:bg-muted",
                          isActive ? "bg-secondary text-foreground" : "text-foreground/90",
                        )
                      }
                      end={item.href === "/"}
                    >
                      <item.icon className="size-[18px] shrink-0" />
                      <span className="truncate">{item.label}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
      </SheetContent>
    </Sheet>
  );
}
